import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const quickLinks = [
  { label: 'Complete Spreadsheet Guide', to: '/allchinabuy-spreadsheet-complete-guide' },
  { label: 'Shipping Cost Guide', to: '/allchinabuy-shipping-cost' },
  { label: 'Coupon Guide', to: '/allchinabuy-coupon' },
];

export default function AllChinaBuyReview() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-20 pb-4 bg-white border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <nav className="flex items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors cursor-pointer">{t('breadcrumb_home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-stone-900 font-medium">AllChinaBuy Review 2026</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-10 shadow-sm">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-stone-100 rounded-full text-stone-600 mb-4">8 min read</span>
            <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">AllChinaBuy Review 2026: Features, Fees & User Experience</h1>
            <div className="flex items-center gap-4 text-sm text-stone-500 mb-6">
              <span>{t('article_last_updated')}: August 25, 2026</span>
              <span>{t('article_author')}: AllChinaBuy Research Team</span>
            </div>

            <div className="prose prose-stone max-w-none">
              <p className="text-stone-600 leading-relaxed mb-4">
                <strong>AllChinaBuy</strong> has established itself as a notable shopping agent for international buyers looking to purchase products from Chinese marketplaces. In this comprehensive 2026 review, we examine the platform's features, fee structure, shipping options, and overall user experience.
              </p>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">What Is AllChinaBuy?</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                AllChinaBuy is a shopping agent service that enables international customers to purchase products from Chinese e-commerce platforms that typically don't ship overseas. The service acts as an intermediary, handling purchases, warehouse storage, quality inspection, and international shipping.
              </p>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">Key Features</h2>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-2 text-stone-600">
                  <i className="ri-check-line text-emerald-500 mt-1"></i>
                  <span>Product purchasing from major Chinese marketplaces</span>
                </li>
                <li className="flex items-start gap-2 text-stone-600">
                  <i className="ri-check-line text-emerald-500 mt-1"></i>
                  <span>Free warehouse storage period</span>
                </li>
                <li className="flex items-start gap-2 text-stone-600">
                  <i className="ri-check-line text-emerald-500 mt-1"></i>
                  <span>Quality check (QC) photo service</span>
                </li>
                <li className="flex items-start gap-2 text-stone-600">
                  <i className="ri-check-line text-emerald-500 mt-1"></i>
                  <span>Package consolidation and repacking</span>
                </li>
                <li className="flex items-start gap-2 text-stone-600">
                  <i className="ri-check-line text-emerald-500 mt-1"></i>
                  <span>Multiple international shipping options</span>
                </li>
              </ul>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">Fee Structure</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                AllChinaBuy employs a transparent fee structure that includes service fees, shipping costs, and optional add-ons. Understanding these fees is crucial for budgeting your purchases effectively. The service fee is typically calculated as a percentage of the item cost, while shipping costs depend on destination, weight, and selected shipping method.
              </p>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">Shipping Experience</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                AllChinaBuy offers multiple shipping lines to accommodate different budgets and delivery timeframes. Options range from economical postal services to express courier options like DHL and FedEx. Delivery times vary significantly based on the chosen method and destination country.
              </p>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">Customer Support</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                The platform provides customer support through multiple channels, including email, live chat, and ticket systems. Response times vary but are generally reasonable for non-urgent inquiries. For complex issues, patience may be required due to time zone differences.
              </p>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">Pros and Cons</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <h4 className="font-semibold text-emerald-900 mb-2">Pros</h4>
                  <ul className="space-y-1.5 text-sm text-emerald-800">
                    <li>Easy access to Chinese marketplaces</li>
                    <li>Quality check photo service</li>
                    <li>Multiple shipping options</li>
                    <li>Spreadsheet resources for product discovery</li>
                    <li>Competitive service fees</li>
                  </ul>
                </div>
                <div className="bg-red-50 rounded-xl p-4">
                  <h4 className="font-semibold text-red-900 mb-2">Cons</h4>
                  <ul className="space-y-1.5 text-sm text-red-800">
                    <li>International shipping can be expensive</li>
                    <li>Customs duties may apply</li>
                    <li>Time zone differences for support</li>
                    <li>QC photos cost extra</li>
                    <li>Return process can be complex</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-xl font-bold text-stone-900 mt-8 mb-3">Final Verdict</h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                AllChinaBuy is a solid choice for international shoppers who want access to Chinese products. The platform offers a good balance of features, reasonable fees, and useful resources like product spreadsheets. While shipping costs remain a consideration for all international shopping agents, AllChinaBuy's transparent pricing and quality check services help mitigate common risks.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-200">
              <h3 className="font-semibold text-stone-900 mb-3">Related Guides</h3>
              <div className="space-y-2">
                {quickLinks.map((link) => (
                  <Link key={link.to} to={link.to} className="block text-sm text-stone-600 hover:text-stone-900 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line mr-1"></i>{link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-8 bg-stone-900 rounded-xl p-6 text-center">
              <p className="text-white mb-4">Ready to explore AllChinaBuy?</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-2.5 bg-white text-stone-900 rounded-lg text-sm font-medium hover:bg-stone-100 transition-colors cursor-pointer">
                AllChinaBuy Spreadsheet <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}